import { createSlice } from "@reduxjs/toolkit";

const isWholesaleType = (type) => {
  if (!type) return false;
  const lower = String(type).toLowerCase();
  return lower === "raw_food" || lower === "wholesale";
};

const getStorageKey = (restaurantId, businessType) => {
  if (isWholesaleType(businessType)) {
    return "restaurantCart_global_wholesale";
  }
  return `restaurantCart_${restaurantId}_${businessType}`;
};

const getCartByRestaurant = (restaurantId, businessType) => {
  const key = getStorageKey(restaurantId, businessType);
  const storedCart = localStorage.getItem(key);
  return storedCart ? JSON.parse(storedCart) : [];
};

const initialState = {
  items: [],
  restaurantId: null,
};

const restaurantCartSlice = createSlice({
  name: "restaurantCart",
  initialState,
  reducers: {
    setRestaurant(state, action) {
      const { restaurantId, businessType } = action.payload;
      if (!restaurantId) return;
      state.restaurantId = restaurantId;
      state.businessType = businessType;
      const storedCart = getCartByRestaurant(restaurantId, businessType);
      state.items = storedCart.map((item) => ({
        ...item,
        businessId: item.businessId || restaurantId,
        businessType: businessType,
        deliveryDateType: item.deliveryDateType || item.detail?.deliveryDateType,
        detail: item.detail || {
          public_price: item.public_price,
          discount: item.discount,
          product_name: item.product_name,
          deliveryDateType: item.deliveryDateType || item.detail?.deliveryDateType,
        },
      }));
    },
        addToCart(state, action) {
      const {
        restaurantId,
        productId,
        quantity,
        public_price,
        discount,
        product_name,
        businessType,
        businessName,
        deliveryDateType,
        extras = [],
      } = action.payload;

      const isWholesale = isWholesaleType(businessType);

      if (
        !state.restaurantId ||
        (!isWholesale && state.restaurantId !== restaurantId) ||
        state.businessType !== businessType
      ) {
        state.restaurantId = restaurantId;
        state.businessType = businessType;
        const storedCart = getCartByRestaurant(restaurantId, businessType);
        state.items = storedCart.map((item) => ({
          ...item,
          businessId: item.businessId || restaurantId,
          businessType: businessType,
          deliveryDateType: item.deliveryDateType || item.detail?.deliveryDateType,
          detail: item.detail || {
            public_price: item.public_price,
            discount: item.discount,
            product_name: item.product_name,
            deliveryDateType: item.deliveryDateType || item.detail?.deliveryDateType,
          },
        }));
      }

      const updatedExtras = extras.map((extra) => {
        if (!extra.name) {
          const extraDetail = extra.item || {};
          extra.name = extraDetail.name || "Unknown Extra";
        }
        return extra;
      });

      const indexProductId = state.items.findIndex(
        (item) => item.productId === productId
      );

      if (indexProductId >= 0) {
        state.items[indexProductId].quantity += quantity;

        const existingExtras = state.items[indexProductId].extras || [];
        updatedExtras.forEach((extra) => {
          const extraIndex = existingExtras.findIndex((e) => e.id === extra.id);
          if (extraIndex >= 0) {
            existingExtras[extraIndex].quantity += extra.quantity;
          } else {
            existingExtras.push(extra);
          }
        });

        state.items[indexProductId].extras = existingExtras;
      } else {
        state.items.push({
          productId,
          businessId: restaurantId,
          businessType: businessType,
          quantity,
          deliveryDateType,
          detail: {
            public_price,
            discount,
            product_name,
            deliveryDateType,
            businessName,
          },
          extras: updatedExtras,
        });
      }

      const key = getStorageKey(state.restaurantId, state.businessType);
      localStorage.setItem(key, JSON.stringify(state.items));
    },

    changeQuantity(state, action) {
      const { productId, quantity, extras } = action.payload;
      const indexProductId = state.items.findIndex(
        (item) => item.productId === productId
      );

      if (indexProductId >= 0) {
        if (quantity > 0) {
          state.items[indexProductId].quantity = quantity;
        } else {
          state.items.splice(indexProductId, 1);
        }

        if (extras) {
          state.items[indexProductId].extras = extras;
        }
      }

      const key = getStorageKey(state.restaurantId, state.businessType);
      localStorage.setItem(key, JSON.stringify(state.items));
    },

    clearCart(state) {
      if (state.businessType) {
        const key = getStorageKey(state.restaurantId, state.businessType);
        localStorage.removeItem(key);
      }
      state.items = [];
      state.restaurantId = null;
      state.businessType = null;
    },
  },
});

export const { setRestaurant, addToCart, changeQuantity, clearCart } =
  restaurantCartSlice.actions;

export default restaurantCartSlice.reducer;
